<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { useOrganizationDocumentStore } from '@/store';
import { useDateTime, can } from '@/composables';
import AddOrganizationDocumentModal from '@/components/UploadModal/AddOrganizationDocumentModal.vue';
import { PERMISSIONS } from '@/enum';
const organizationDocumentsStore = useOrganizationDocumentStore();
const documentModalOpen = ref(false);

onMounted(() => getDocuments());

const getDocuments = async () => {
  const filter = { tag: 'others' };
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_INDEX])) await organizationDocumentsStore.getGeneralDocuments(filter);
  else doFailUnauthorized('view');
};

const openDocumentUploader = () => (documentModalOpen.value = true);

const doToast = (summary: string, detail: string) => {
  organizationDocumentsStore.doToast(detail, summary);
};

const doFailUnauthorized = (action: string) => {
  organizationDocumentsStore.doToast(`User unauthorized to ${action} documents.`, 'Error');
};

const onSaveAdd = () => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_STORE])) {
    getDocuments();
    doToast('Added', 'New Document has been added');
  } else {
    doFailUnauthorized('upload');
  }
};

const onCancelAdd = () => (documentModalOpen.value = false);

const onDeleteDocument: (id: number) => Promise<void> = async (id) => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DESTROY])) {
    await organizationDocumentsStore.deleteGeneralDocument(id);
    getDocuments();
    doToast('Removed', 'Document has been removed');
  } else {
    doFailUnauthorized('delete');
  }
};

const onDownloadDocument = async (id: number, name: string) => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DOWNLOAD])) {
    await organizationDocumentsStore.downloadGeneralDocument(id, name);
    doToast('Downloaded', 'Document successfully downloaded');
  } else {
    doFailUnauthorized('download');
  }
};
</script>

<template>
  <Card>
    <template #header>
      <div class="ml-auto py-3">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_DOCUMENTS_STORE])"
          type="primary"
          text="Upload Document"
          icon-left="cloud-upload"
          @click="openDocumentUploader"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="organizationDocumentsStore.generalDocuments.data"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="organizationDocumentsStore.loading"
        responsive-layout="scroll"
        scroll-height="440px"
      >
        <template v-if="organizationDocumentsStore.generalDocuments.data.length">
          <Column field="title" header="Title" header-style="color: #3B4242;">
            <template #body="{ data }">
              <span
                class="download cursor-pointer"
                @click="onDownloadDocument(data.id, data.name)"
                v-text="data.name"
              />
            </template>
          </Column>
          <Column
            field="description"
            header="Description"
            header-class="justify-content-center"
            body-class="justify-content-center text-align-center"
            body-style="font-size: .8725rem;"
          >
            <template #body="{ data }">
              <span v-text="data.description" />
            </template>
          </Column>
          <Column
            field="created_at"
            header="Date Created"
            header-class="justify-content-center"
            body-class="justify-content-center"
            body-style="font-size: .8725rem;"
          >
            <template #body="{ data }">
              <span v-text="useDateTime().formatIntlDate(data.created_at)" />
            </template>
          </Column>
          <Column
            body-class="justify-content-center"
            body-style="font-size: .8725rem;"
            header-class="justify-content-center"
            header="Actions"
          >
            <template #body="{ data }">
              <AppButton
                v-if="can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DOWNLOAD])"
                type="text"
                size="large"
                icon-left="cloud_download"
                class="text-600 cloud-download"
                @click="onDownloadDocument(data.id, data.name)"
              />
              <AppButton
                v-if="can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DESTROY])"
                type="text"
                size="large"
                icon-left="delete"
                class="text-600"
                @click="onDeleteDocument(data.id)"
              />
            </template>
          </Column>
        </template>
        <template #empty>No documents</template>
      </DataTable>
      <AddOrganizationDocumentModal
        v-if="documentModalOpen && can([PERMISSIONS.ORGANIZATION_DOCUMENTS_STORE])"
        title="Upload Document"
        :billing="false"
        @cancel="onCancelAdd"
        @save="onSaveAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.download {
  color: var(--primary-700);
}

.p-card {
  min-width: calc(100% - 1.5rem);
  min-height: 200px;

  :deep(.button.cloud-download .button-inner .button-text .button-icon-left) {
    max-width: 18px;
  }
}
</style>
